import { DEBOUNCE_DELAY_STICKY_HEADER } from '../tools/constants';
import isServer from '../tools/isServer';

/**
 * Sticky Header Service
 */
class StickyHeaderService {
  constructor() {
    this.state = {
      isNavbarVisible: true,
      isFiltersSticky: false, // Start as false to prevent transform on page load
      lastScrollDirection: null,
    };
    this.subscribers = new Set();
    this.isServerSide = isServer();
    this.lastStateHash = StickyHeaderService.computeStateHash(this.state);

    // Add debouncing to prevent flickering
    this.updateTimeout = null;
    // 50ms debounce to prevent rapid state changes
    this.debounceDelay = DEBOUNCE_DELAY_STICKY_HEADER;
  }

  /**
   * Compute hash for state comparison using JSON serialization
   * @param {Object} state - State object to hash
   * @returns {string} State hash
   */
  static computeStateHash(state) {
    try {
      return JSON.stringify(state);
    } catch (error) {
      console.warn('[StickyHeaderService] Error serializing state:', error);
      return '';
    }
  }

  /**
   * Check if state has changed using JSON comparison
   * @param {Object} newState - New state to compare
   * @returns {boolean} True if state changed
   */
  hasStateChanged(newState) {
    const newHash = StickyHeaderService.computeStateHash(newState);
    return newHash !== this.lastStateHash;
  }

  /**
   * Get current state
   * @returns {Object} Current state copy
   */
  getState() {
    return { ...this.state };
  }

  /**
   * Update state and notify subscribers if changed (with debouncing)
   * @param {Object} newState - New state to merge
   */
  updateState(newState) {
    if (this.isServerSide) return;

    // Clear existing timeout
    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout);
    }

    // Debounce the update to prevent flickering
    this.updateTimeout = setTimeout(() => {
      this.performStateUpdate(newState);
    }, this.debounceDelay);
  }

  /**
   * Perform the actual state update (internal method)
   * @param {Object} newState - New state to merge
   * @param {string} source - Source of the update
   */
  performStateUpdate(newState) {
    const updatedState = {
      ...this.state,
      ...newState,
    };

    // Only update and notify if state actually changed
    if (this.hasStateChanged(updatedState)) {
      this.state = updatedState;
      this.lastStateHash = StickyHeaderService.computeStateHash(this.state);
      this.notifySubscribers();
    }
  }

  /**
   * Handle scroll direction changes (with improved logic to prevent flickering)
   * @param {string} direction - Scroll direction ('up' or 'down')
   */
  handleScrollDirection(direction) {
    if (this.isServerSide) return;

    // Prevent rapid direction changes that cause flickering
    if (this.state.lastScrollDirection === direction) {
      return; // Skip if direction hasn't changed
    }

    const newState = { lastScrollDirection: direction };

    if (direction === 'up') {
      // On scroll up: Show navbar and filters as sticky (filters below navbar)
      newState.isNavbarVisible = true;
      newState.isFiltersSticky = true;
    } else if (direction === 'down') {
      // On scroll down: Hide navbar, show sticky filters
      newState.isNavbarVisible = false;
      newState.isFiltersSticky = true;
    }

    this.updateState(newState);
  }

  /**
   * Subscribe to state changes
   * @param {Function} callback - Function to call when state changes
   * @returns {Function} Unsubscribe function
   */
  subscribe(callback) {
    if (this.isServerSide) {
      return () => {}; // Return no-op for SSR
    }

    this.subscribers.add(callback);

    // Immediately call with current state
    callback(this.getState());

    return () => {
      this.subscribers.delete(callback);
    };
  }

  /**
   * Notify all subscribers of state changes
   */
  notifySubscribers() {
    const currentState = this.getState();
    this.subscribers.forEach((callback) => {
      try {
        callback(currentState);
      } catch (error) {
        console.error('[StickyHeaderService] Error in subscriber callback:', error);
      }
    });
  }

  /**
   * Reset state to defaults
   */
  reset() {
    const defaultState = {
      isNavbarVisible: true,
      isFiltersSticky: false,
      lastScrollDirection: null,
    };
    this.updateState(defaultState);
  }

  /**
   * Cleanup resources
   */
  cleanup() {
    if (this.isServerSide) return;

    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout);
      this.updateTimeout = null;
    }
    this.subscribers.clear();
  }
}

// Create singleton instance
const stickyHeaderService = new StickyHeaderService();

export default stickyHeaderService;
